import type { VacancyFilterKey, VacancyListKind } from "../builder/constants";
import type { DraftBody, DraftRules } from "../builder/mappers";
import type { CountRulePayload } from "../builder/schemas";

type SegmentTabVacanciesParams = {
  rules: DraftRules;
  list: VacancyListKind;
  search?: string;
  descending: boolean;
  filters: Record<VacancyFilterKey, string[]>;
};

type SegmentVacancyFiltersParams = {
  body: DraftBody;
  list: VacancyListKind;
  search?: string;
};

type SegmentVacancyTotalParams = SegmentVacancyFiltersParams & {
  filters: Partial<Record<VacancyFilterKey, string[]>>;
};

type SegmentListParams = {
  clientId?: string;
  descending?: boolean;
  orderBy?: string;
  page?: number;
  pageSize?: number;
  search?: string;
};

export const segmentsQueryKeys = {
  all: ["segments"] as const,
  lists: () => [...segmentsQueryKeys.all, "list"] as const,
  list: (params: SegmentListParams) =>
    [
      ...segmentsQueryKeys.lists(),
      params.clientId,
      params.descending,
      params.orderBy,
      params.page,
      params.pageSize,
      params.search,
    ] as const,
  /** Prefix of everything shown about one segment: its detail, activity and vacancy preview. */
  segment: (segmentId?: string) => [...segmentsQueryKeys.all, "detail", segmentId] as const,
  detail: (segmentId?: string, clientId?: string) =>
    [...segmentsQueryKeys.segment(segmentId), clientId] as const,
  activity: (segmentId?: string) =>
    [...segmentsQueryKeys.detail(segmentId), "activity"] as const,
  fields: () => [...segmentsQueryKeys.all, "fields"] as const,
  tourLink: (clientId?: string) =>
    [...segmentsQueryKeys.all, "tour-link", clientId] as const,
  counts: () => [...segmentsQueryKeys.all, "counts"] as const,
  ruleCount: (clientId: string | undefined, rule: CountRulePayload) =>
    [...segmentsQueryKeys.counts(), "rule", clientId, rule] as const,
  vacancyPreview: (segmentId?: string, clientId?: string) =>
    [...segmentsQueryKeys.detail(segmentId, clientId), "vacancies"] as const,
  draft: () => [...segmentsQueryKeys.all, "draft"] as const,
  tabVacancies: (params: SegmentTabVacanciesParams) =>
    [...segmentsQueryKeys.draft(), "vacancies", params] as const,
  vacancyFilters: (params: SegmentVacancyFiltersParams) =>
    [...segmentsQueryKeys.draft(), "vacancyFilters", params] as const,
  vacancyTotal: (params: SegmentVacancyTotalParams) =>
    [...segmentsQueryKeys.draft(), "vacancyTotal", params] as const,
  listCounts: (body: DraftBody) =>
    [...segmentsQueryKeys.draft(), "listCounts", body] as const,
  overrideImpact: (segmentId: string | undefined, body: DraftBody) =>
    [...segmentsQueryKeys.draft(), "overrideImpact", segmentId, body] as const,
  historyOf: (segmentId: string) => [...segmentsQueryKeys.all, "history", segmentId] as const,
  history: (
    segmentId: string,
    from: Date,
    to?: Date,
    pageSize?: number,
    search?: string,
    changeTypes?: string[],
  ) =>
    [
      ...segmentsQueryKeys.historyOf(segmentId),
      from.toISOString(),
      to?.toISOString(),
      pageSize,
      search,
      changeTypes,
    ] as const,
};
